import React, { useState, useEffect, useRef, useContext } from 'react';
import {
  BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend, ResponsiveContainer
} from 'recharts';

import Tekniikkataulut from './Tekniikkataulut';
import TutkimustarpeetTaulu from './Tutkimustarpeettaulu';
import PiechartPTS from './PiechartPTS';
import html2canvas from 'html2canvas';
import ToastMessage from '../ToastMessage';
import { saveImage } from '../../assets/imageDb';
import { usePTS } from '../../context/PTSContext';
import Taloustaulu from './Taloustaulu';
import { useRakennusData } from '../../context/RakennusDataContext';
import { exportYhteenvetoToExcel } from '../utils/exportYhteenvetoToExcel';




export default function PTSLongTermTable({data, onSectionsChange, setPtsImages = null, onBackground = false }) {

  const {
    rakennusData: rakennus,
  } = useRakennusData();

  const {
    years: allYears,
    currentYear,
    ptsData,
    chartData,
    tekniikkaData,
    lviData,
    sahkoData,
    tutkimusData,
    tekniikkaYhteensa,
    lviYhteensa,
    sahkoYhteensa,
    tutkimusYhteensa,
    toast,
    setToast,
  } = usePTS();

  const kiinteistotunnus = rakennus?.kiinteistotunnus
  const showPart = data?.showPart || 'all'
  // Only swaps the header label text (ALV 0% vs sis. Alv 25,5%) — no
  // recalculation of any figures.
  const showAlvZero = !!data?.showAlvZero
  // Display-only truncation — the underlying 11-value arrays are untouched,
  // so turning this back off shows the last year again unchanged.
  const hide11thYear = !!data?.hide11thYear
  const years = hide11thYear ? allYears.slice(0, 10) : allYears



  useEffect(() => {
    console.log("Löydetty kiinteistötunnus", kiinteistotunnus)
  }, [kiinteistotunnus])
  

  useEffect(() => {   
    if (!onSectionsChange) return;
    const pick = (arr = []) => arr.map(s => s.name || s.header).filter(Boolean);

    const newSections = {
      'Rakennetekniikka': pick(tekniikkaData),
      'LVI Järjestelmät': pick(lviData),
      'Sähköjärjestelmät': pick(sahkoData),
      'Lisätutkimukset': pick(tutkimusData).length
    ? pick(tutkimusData)
    : ['Tutkimustarpeet'],
    };

    onSectionsChange(newSections);
  }, [
    JSON.stringify(tekniikkaData),
    JSON.stringify(lviData),
    JSON.stringify(sahkoData),
    JSON.stringify(tutkimusData),
  ]);



  const yhteensaRef = useRef(null);
  const lisatutkimuksetRef = useRef(null)
  const rakennetekniikkaRef = useRef(null)
  const lvitekniikkaRef = useRef(null)
  const sahkotekniikkaRef = useRef(null)
  const talousRef = useRef(null)

  // Kuvaajien ja taulujen "kuvaus" raporttia varten:
  const refs = [yhteensaRef, lisatutkimuksetRef, rakennetekniikkaRef, lvitekniikkaRef, sahkotekniikkaRef, talousRef];

  const getColumnTotals = (subcategory) => {
    if (!subcategory || !subcategory.items) return [];
    const numYears = Math.min(subcategory.items[0]?.values?.length || 0, years.length);

    return Array.from({ length: numYears }, (_, yearIdx) => {
      return subcategory.items
        .filter(i => i.label !== 'Yhteensä')
        .reduce((sum, item) => {
          const num = parseFloat(item.values[yearIdx]);
          return !isNaN(num) ? sum + num : sum;
        }, 0);
    });
  };


return (
  <div className="container-fluid mb-4 pts-tables-container">

    {!onBackground && setPtsImages !== null && (
      <div className="mb-3 text-center">
        <button
          className="btn btn-success"
          onClick={async () => {
            if (!refs.length) return;
            try {
              const capturedImages = [];
              for (const ref of refs) {
                if (!ref.current) continue;
                const canvas = await html2canvas(ref.current, { scale: 2, useCORS: true });
                const blob = await new Promise(resolve => canvas.toBlob(resolve, "image/png"));
                const imageId = await saveImage(blob);
                capturedImages.push({ imageId, caption: '' });
              }
              if (setPtsImages) setPtsImages(capturedImages);
              setToast({ show: true, message: '📸 Kaikki kuvat tallennettu!', bg: 'success' });
            } catch (err) {
              console.error("Error capturing images:", err);
              setToast({ show: true, message: '❌ Kuvien tallennus epäonnistui', bg: 'danger' });
            }
          }}
        >
          🔄 Päivitä PTS-kuvat
        </button>
      </div>
    )}

    <div className="mx-auto w-100 w-md-100" style={{ maxWidth: '960px' }}>
      <ToastMessage
        show={toast.show}
        message={toast.message}
        bg={toast.bg}
        onClose={() => setToast({ ...toast, show: false })}
      />

      {/* ===== Flat layout instead of accordion ===== */}
      {ptsData.map((cat, catIdx) => (
        <React.Fragment key={catIdx}>
          {cat.subcategories.map((sub, subIdx) => (
            <div key={subIdx} className="mb-4 mt-5">
              {/* {sub.name && <h3 className="mb-2">{sub.name}</h3>} */}

              {(showPart === 'all' || showPart === 'pts-yhteenveto') && (
                  <div ref={yhteensaRef} style={{ position: 'relative' }}>
                    <div data-html2canvas-ignore style={{ position: 'absolute', top: 8, right: 8, zIndex: 10 }}>
                      <button
                        className="btn btn-sm btn-outline-success"
                        onClick={() =>
                          exportYhteenvetoToExcel({
                            sub,
                            years,
                            currentYear,
                            tekniikkaYhteensa,
                            lviYhteensa,
                            sahkoYhteensa,
                            tutkimusYhteensa
                          })
                        }
                      >
                        Lataa Excel
                      </button>
                    </div>
                  
                  {chartData.length > 0 ? (
                    <div style={{ width: '100%', height: '400px' }}>
                      <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={chartData} margin={{ top: 10, right: 20, left: 5, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="year" tick={{ fontSize: 16, fontFamily: 'Lato', fontWeight: 'bold' }} />
                          <YAxis tick={{ fontSize: 16, fontFamily: 'Lato', fontWeight: 'bold' }} />
                          <Tooltip wrapperStyle={{ fontFamily: 'Lato', fontSize: '16px', fontWeight: 'bold' }} />
                          <Legend wrapperStyle={{ fontFamily: 'Lato', fontSize: '16px', fontWeight: 'bold' }} />
                          <Bar dataKey="Lisätutkimukset" fill="#2F5930" stackId="a" />
                          <Bar dataKey="Rakennetekniikka" fill="#7AA668" stackId="a" />
                          <Bar dataKey="LVI Järjestelmät" fill="#A7BFA2" stackId="a" />
                          <Bar dataKey="Sähköjärjestelmät" fill="#C8D1BC" stackId="a" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <p className="small">No ptsData available for the bar chart.</p>
                  )}

                  {(tekniikkaYhteensa || lviYhteensa || sahkoYhteensa || tutkimusYhteensa) && (
                    <div className='py-2' style={{ maxWidth: '400px', margin: '0 auto' }}>
                      <PiechartPTS
                        tekniikkaYhteensa={tekniikkaYhteensa}
                        lviYhteensa={lviYhteensa}
                        sahkoYhteensa={sahkoYhteensa}
                        tutkimusYhteensa={tutkimusYhteensa}
                      />
                    </div>
                  )}

                {/* Yhteenveto taulukko */}
                 <div className="table-responsive ptstaulut pts-react-to-edit">

                    <table className="table table-sm table-borderless table-striped mb-0 small">
                      <thead>
                        <tr>
                          <th colSpan={years.length + 2} className="bg-success text-white p-1">
                            <div className="d-flex justify-content-between">
                              <div className="fw-bold">Toimenpide-ehdotukset yhteensä</div>
                              <div className="small text-end">
                                {showAlvZero
                                  ? `Kustannusarvio (x 1000€) Kustannustaso ${currentYear} ALV 0%`
                                  : `Kustannusarvio (x 1000€) Kustannustaso ${currentYear} sis. Alv 25,5%`}
                              </div>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <thead>
                        <tr>
                          <th className="bg-success text-white text-start p-1">Osa-alue</th>
                          <th className="bg-success text-white text-center p-1">Yhteensä</th>
                            {years.map((year) => (
                              <th key={year} className="bg-success text-white text-center px-1 fw-bold">{year}</th>
                            ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sub.items.filter(item => item.label !== 'Yhteensä').map((item, itemIdx) => {
                          const rowTotal = item.values.slice(0, years.length).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
                          const valuesToShow = (sub.name === 'Yhteenvetotaulukko'
                            ? item.label === 'Rakennetekniikka' ? tekniikkaYhteensa
                              : item.label === 'LVI Järjestelmät' ? lviYhteensa
                              : item.label === 'Sähköjärjestelmät' ? sahkoYhteensa
                              : item.label === 'Lisätutkimukset' ? tutkimusYhteensa
                              : item.values
                            : item.values).slice(0, years.length);

                          return (
                            <tr key={itemIdx}>
                              <td className="text-start p-1">{item.label}</td>
                              <td className="text-center font-monospace p-1">{rowTotal}</td>
                              {valuesToShow.map((val, yearIdx) => {
                                const numVal = parseFloat(val);
                                return (
                                  <td key={yearIdx} className="text-center px-1 p-1">
                                    {sub.name === 'Yhteenvetotaulukko' ? (
                                      numVal > 0 ? numVal : "" // only show numbers > 0
                                    ) : (
                                      <input
                                        type="text"
                                        value={numVal > 0 ? val : ""}
                                        className="form-control form-control-sm text-center p-1"
                                        // optionally, disable editing if value is 0
                                      />
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="fw-bold">
                          <td className="bg-success text-white text-start p-1">YHTEENSÄ</td>
                          <td className="bg-success text-white text-center font-monospace p-1">
                            {getColumnTotals(sub).reduce((a, b) => a + b, 0)}
                          </td>
                            {getColumnTotals(sub).map((colSum, idx) => (
                              <td key={idx} className="bg-success text-white text-center font-monospace px-1 p-1">{colSum}</td>
                            ))}
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ))}
        </React.Fragment>
      ))}

      {(showPart === 'all' || showPart === 'pts-talous') && (
        <Taloustaulu
          ref={talousRef}
          onBackground={onBackground}
        />
      )}

      {(showPart === 'all' || showPart === 'pts-tutkimustarpeet') && (
        <TutkimustarpeetTaulu
          ref={lisatutkimuksetRef}
          showAlvZero={showAlvZero}
          hide11thYear={hide11thYear}
        />
      )}

      {(showPart === 'all' || showPart === 'pts-rakenne') && (
        <Tekniikkataulut
          type={'Rakennetekniikka'}
          ref={rakennetekniikkaRef}
          hideKuntoluokka={data?.hideKuntoluokka}
          showAlvZero={data?.showAlvZero}
          hide11thYear={hide11thYear}
        />
      )}

      {(showPart === 'all' || showPart === 'pts-lvi') && (
        <Tekniikkataulut
          type={'LVI Järjestelmät'}
          ref={lvitekniikkaRef}
          hideKuntoluokka={data?.hideKuntoluokka}
          showAlvZero={data?.showAlvZero}
          hide11thYear={hide11thYear}
        />
      )}

      {(showPart === 'all' || showPart === 'pts-sähkö') && (
        <Tekniikkataulut

          type={'Sähköjärjestelmät'}
          ref={sahkotekniikkaRef}
          hideKuntoluokka={data?.hideKuntoluokka}
          showAlvZero={data?.showAlvZero}
          hide11thYear={hide11thYear}
        />
      )}
    </div>

    <style>{`
      .pts-tables-container {
        font-size: 0.85rem; 
        transform: scale(0.95); 
        transform-origin: top left;
      }

      .pts-tables-container table th,
      .pts-tables-container table td {
        padding: 0.25rem 0.5rem;
      }

      .pts-tables-container .form-control {
        font-size: 0.85rem;
      }
    `}</style>
  </div>
);

}
